import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getCompany, getCompanyExtras, updateJobTypes, updateCostCategories, updateLeadSources, updatePriceBook, updateNotificationRouting } from '../services/companyService';
import {
  notificationRoutingNeedsSeed, buildDefaultNotificationRouting, normalizeNotificationRouting,
} from '../utils/notificationRoutingUtils';
import { jobTypesNeedSeedOrMigration, normalizeJobTypes } from '../utils/jobTypesUtils';
import { costCategoriesNeedSeed } from '../utils/costCategoriesUtils';
import { leadSourcesNeedSeed } from '../utils/leadSourcesUtils';
import { priceBookNeedsSeed, normalizePriceBook } from '../utils/priceBookUtils';
import {
  buildDefaultJobTypesForIndustry,
  buildDefaultCostCategoriesForIndustry,
  buildDefaultLeadSourcesForIndustry,
  buildDefaultPriceBookForIndustry,
} from '../utils/industryRegistry';

const CompanyContext = createContext();

export function useCompany() {
  return useContext(CompanyContext);
}

export function CompanyProvider({ children }) {
  const { userProfile } = useAuth();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    // WHICHEVER COMPANY THIS EFFECT IS ABOUT, IT HAS NOT BEEN LOADED YET.
    // `loading` used to mean "a fetch has finished at some point", not "the
    // CURRENT companyId is loaded": it started true and was set false by the
    // first run, and no later run ever put it back. Between a sign-out and the
    // next sign-in — or an account switch — that left `loading: false` sitting
    // beside the PREVIOUS company's document, so a route gate reading
    // `company.onboardingComplete` was reading the wrong company's answer while
    // believing it was settled. Resetting both is what makes the flag mean what
    // its readers assume.
    setLoading(true);
    setCompany(null);

    const fetchCompany = async () => {
      if (!userProfile?.companyId) {
        setLoading(false);
        return;
      }

      // Stage 1: core company doc (small) — unblocks the kanban immediately.
      const core = await getCompany(userProfile.companyId);
      if (cancelled) return;
      if (core) setCompany(core);
      setLoading(false);

      // Stage 2: extras subdoc (jobTypes, customFields, pricingPresets,
      // companyDocumentFields) — populated lazily.
      if (!core) return;
      try {
        const extras = await getCompanyExtras(core.id);
        if (cancelled) return;
        // Every seed below is INDUSTRY-AWARE. A company's `industry` decides
        // which vertical's defaults it gets (industryRegistry.js); companies
        // predating the field resolve to Window & Door. Without this, a SaaS
        // company would be seeded/backfilled with window-and-door job types.
        const industryId = core.industry;
        let jobTypes = extras.jobTypes || [];
        if (jobTypesNeedSeedOrMigration(jobTypes)) {
          jobTypes = normalizeJobTypes(jobTypes, buildDefaultJobTypesForIndustry(industryId));
          try {
            await updateJobTypes(core.id, jobTypes);
          } catch (err) {
            console.error('Failed to seed job types:', err);
          }
        }
        // Seed/backfill cost categories the same way (idempotent — only writes
        // when missing). Non-owners get the defaults in-memory; the stable ids
        // mean any later owner-side persist matches exactly.
        let costCategories = extras.costCategories || [];
        if (costCategoriesNeedSeed(costCategories)) {
          costCategories = buildDefaultCostCategoriesForIndustry(industryId);
          try {
            await updateCostCategories(core.id, costCategories);
          } catch (err) {
            console.error('Failed to seed cost categories:', err);
          }
        }
        // Seed/backfill lead sources the same idempotent way — non-owners get
        // the defaults in-memory; stable ids mean a later owner-side persist
        // matches exactly.
        let leadSources = extras.leadSources || [];
        if (leadSourcesNeedSeed(leadSources)) {
          leadSources = buildDefaultLeadSourcesForIndustry(industryId);
          try {
            await updateLeadSources(core.id, leadSources);
          } catch (err) {
            console.error('Failed to seed lead sources:', err);
          }
        }
        // Seed/backfill the manufacturer price book the same idempotent way.
        // DIFFERENT EMPTY RULE from its siblings on purpose: seeding is keyed on
        // the key being ABSENT, never on the list being empty, so a company that
        // deliberately deleted every manufacturer does not get them back on the
        // next load. Normalized on the way in either way, so a partially-written
        // or hand-edited book can never reach the UI misshapen.
        let priceBook = extras.priceBook;
        if (priceBookNeedsSeed(priceBook)) {
          priceBook = buildDefaultPriceBookForIndustry(industryId);
          try {
            await updatePriceBook(core.id, priceBook);
          } catch (err) {
            console.error('Failed to seed price book:', err);
          }
        } else {
          priceBook = normalizePriceBook(priceBook);
        }
        // Seed/backfill notification routing the same idempotent way, with the
        // price book's ABSENT-KEY rule rather than the empty-list rule: a
        // company that deliberately disabled every event must not have the
        // defaults handed back on the next load.
        //
        // Nothing DEPENDS on this write. functions/notificationRouting.js
        // resolves the same defaults for a company with no stored config, so
        // notifications work from the first minute whether or not the seed
        // landed — this exists so the Settings UI and the stored record agree.
        let notificationRouting = extras.notificationRouting;
        if (notificationRoutingNeedsSeed(notificationRouting)) {
          notificationRouting = buildDefaultNotificationRouting();
          try {
            await updateNotificationRouting(core.id, notificationRouting);
          } catch (err) {
            console.error('Failed to seed notification routing:', err);
          }
        } else {
          notificationRouting = normalizeNotificationRouting(notificationRouting);
        }
        if (cancelled) return;
        setCompany(prev => (prev ? { ...prev, ...extras, jobTypes, costCategories, leadSources, priceBook, notificationRouting } : prev));
      } catch (err) {
        console.error('Failed to load company extras:', err);
      }
    };

    if (userProfile !== null) {
      // A REJECTION HERE MUST NOT LOOK LIKE AN ANSWER. getCompany() throws when
      // Firestore is unreachable; unhandled, that left the promise rejected and
      // `loading` stuck true by accident. It stays true on purpose now — we
      // genuinely do not know this company's onboardingComplete, and
      // useAuthDestination treats unknown as PENDING rather than resolving it
      // to "not onboarded" and routing into onboarding. Recovery is a reload;
      // this is a one-shot getDoc, not a listener.
      fetchCompany().catch((err) => {
        console.error('Failed to load company:', err);
      });
    }

    return () => { cancelled = true; };
    // Key on companyId, not the whole userProfile object. AuthContext rebuilds
    // userProfile (new object) on every user-doc write — including a Day/Night
    // toggle, which persists themeMode to the user doc. Depending on the object
    // re-fetched the entire company (core + extras) on each theme switch; the
    // companyId is what actually determines which company to load.
  }, [userProfile?.companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    company,
    loading,
    setCompany,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}
